import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { API_BASE_URL } from "../lib/env";
import { Button } from "./ui";
import { TurnstileWidget } from "./TurnstileWidget";
import { isCaptchaRejection } from "../lib/captcha-rejection";

const DAYS = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
] as const;
const TIMES = [
  { value: "morning", label: "Morning", hint: "8-11am" },
  { value: "midday", label: "Midday", hint: "11am-2pm" },
  { value: "afternoon", label: "Afternoon", hint: "2-5pm" },
] as const;
const DAY_VALUES = ["mon", "tue", "wed", "thu", "fri"] as const;
const TIME_VALUES = ["morning", "midday", "afternoon"] as const;

function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function timeZoneOptions(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    return fn ? fn("timeZone") : [];
  } catch {
    return [];
  }
}

const DemoSchema = z.object({
  name: z.string().min(2, "Please enter your name").max(120),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().max(40).optional().or(z.literal("")),
  organization: z.string().min(2, "Organization required").max(200),
  facility_type: z.enum(["snf", "asc", "dialysis", "other"], {
    errorMap: () => ({ message: "Pick a facility type" }),
  }),
  bed_count: z.string().max(20).optional().or(z.literal("")),
  message: z.string().max(2000).optional().or(z.literal("")),
  sms_consent: z.boolean().optional(),
  // Availability (all optional) — the owner picks a slot from these.
  preferred_days: z.array(z.enum(DAY_VALUES)).optional(),
  preferred_times: z.array(z.enum(TIME_VALUES)).optional(),
  availability_note: z.string().max(500, "Keep it under 500 characters").optional().or(z.literal("")),
  timezone: z.string().max(64).optional().or(z.literal("")),
  website: z.string().max(200).optional().or(z.literal("")), // honeypot
});

// sms_consent HISTORY, because it has flipped on conflicting guidance from
// the same Twilio reviewer four days apart and will likely be revisited:
//
//   2026-08-10 (Twilio, 1st): "the consent checkbox must be mandatory... will
//     result in campaign rejection" if optional.
//   2026-08-10 (us): removed the checkbox from this form entirely rather than
//     making it mandatory here. This is a demo-request form; requiring consent
//     to ask for a demo would make consent a condition of service — the TCPA
//     problem the mandatory-checkbox rule exists to prevent, and it contradicts
//     "Consent is not a condition of any purchase" on the dedicated opt-in page.
//   2026-08-11 (Twilio, 2nd): reversed — "the checkbox on your SMS opt-in form
//     should be optional... may be considered non-compliant" if mandatory.
//     "Users should be able to submit the form whether or not they check it."
//
// The 2nd instruction is the correct, general CTIA/TCPA rule (consent must
// not be a condition of using a form). It's why the box is back here —
// unchecked by default, optional() on the schema, submission never blocked.
//
// It initially read as NOT applying to /sms-opt-in, on the theory that a page
// whose only purpose is consenting has nothing else to condition. That theory
// did not survive contact with GovMatrixIQ's own approved opt-in page, which
// has the identical shape and was rejected for this identical mandatory
// pattern before being fixed to match round 2 and approved (govmatrixiq-
// landing PR #30). /sms-opt-in now matches it: optional, unchecked by
// default, and onSubmit returns before the network call (no lead, no text)
// when unchecked — see the sms_consent history comment in SmsOptIn.tsx.

export type DemoFormData = z.infer<typeof DemoSchema>;

const inputCls =
  "block w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-forest-700";

const labelCls =
  "block text-sm font-semibold text-ink-800 mb-1.5";

const chipCls =
  "inline-flex cursor-pointer items-center rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-ink-700 has-[:checked]:border-forest-700 has-[:checked]:bg-forest-50 has-[:checked]:text-forest-800 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-forest-700";

export function DemoForm({
  source = "demo",
  endpoint = "demo-request",
}: {
  source?: string;
  endpoint?: "demo-request" | "contact";
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<DemoFormData>({
    resolver: zodResolver(DemoSchema),
    defaultValues: {
      facility_type: "snf",
      website: "",
      // Array defaults keep react-hook-form's checkbox groups as string[]
      // (never `false`) so the schema's array type always matches.
      preferred_days: [],
      preferred_times: [],
      timezone: detectTimeZone(),
    },
  });
  const [tzOptions] = useState(timeZoneOptions);
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [captchaToken, setCaptchaToken] = useState("");
  // Bumped when the API rejects the token, to force a fresh challenge.
  const [captchaReset, setCaptchaReset] = useState(0);

  async function onSubmit(data: DemoFormData) {
    // NO CLIENT-SIDE SITEVERIFY. The API is the single redeemer.
    //
    // This used to call verifyTurnstileViaWorker(captchaToken) and then POST
    // THE SAME token to /v1/marketing/*. Turnstile tokens are single-use, so
    // the API's redemption would have returned timeout-or-duplicate the moment
    // TURNSTILE_ENFORCEMENT_ENABLED was set to 1 — every genuine submission
    // rejected, and only once that flag was on, which is why it never fired.
    // Same bug LoginPage.tsx already fixed; it had not reached the landing app.
    //
    // Verifying server-side is also the stronger gate: a bot can POST the API
    // directly and skip any browser-side check.
    try {
      const res = await fetch(`${API_BASE_URL}/v1/marketing/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...data, source, captcha_token: captchaToken }),
      });
      if (!res.ok) {
        // A rejected token is SPENT. Clear it and make the widget mint a new
        // one, or every retry resends the dead token and fails again for a
        // reason the user cannot see.
        if (await isCaptchaRejection(res)) {
          setCaptchaToken("");
          setCaptchaReset((n) => n + 1);
          setStatus("err");
          return;
        }
        throw new Error(`status_${res.status}`);
      }
      setStatus("ok");
      reset();
      setCaptchaToken("");
    } catch {
      setStatus("err");
    }
  }

  if (status === "ok") {
    return (
      <div
        role="status"
        className="rounded-2xl border border-forest-200 bg-forest-50 p-8 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-forest-700" aria-hidden />
        <h3 className="mt-4 text-xl font-bold text-ink-900">Thanks — we’ll be in touch.</h3>
        <p className="mt-2 text-ink-700">
          A member of the WeConsult Rx team will reach out within one business day.
        </p>
      </div>
    );
  }

  return (
    <form method="post" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      {/* Honeypot */}
      <label className="sr-only" aria-hidden="true">
        Website
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register("website")}
          style={{ display: "none", position: "absolute", left: "-9999px" }}
        />
      </label>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="name" className={labelCls}>
            Full name
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            aria-invalid={!!errors.name}
            className={inputCls}
            {...register("name")}
          />
          {errors.name ? <p className="mt-1 text-xs text-red-600">{errors.name.message}</p> : null}
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>
            Work email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            className={inputCls}
            {...register("email")}
          />
          {errors.email ? <p className="mt-1 text-xs text-red-600">{errors.email.message}</p> : null}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="phone" className={labelCls}>
            Phone (optional)
          </label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            className={inputCls}
            {...register("phone")}
          />
        </div>
        <div>
          <label htmlFor="organization" className={labelCls}>
            Organization
          </label>
          <input
            id="organization"
            type="text"
            autoComplete="organization"
            aria-invalid={!!errors.organization}
            className={inputCls}
            {...register("organization")}
          />
          {errors.organization ? (
            <p className="mt-1 text-xs text-red-600">{errors.organization.message}</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="facility_type" className={labelCls}>
            Primary facility type
          </label>
          <select
            id="facility_type"
            aria-invalid={!!errors.facility_type}
            className={inputCls}
            {...register("facility_type")}
          >
            <option value="snf">Skilled nursing (SNF)</option>
            <option value="asc">Ambulatory surgical center (ASC)</option>
            <option value="dialysis">Dialysis</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="bed_count" className={labelCls}>
            Bed count / annual cases (optional)
          </label>
          <input id="bed_count" type="text" className={inputCls} {...register("bed_count")} />
        </div>
      </div>

      <fieldset className="space-y-3">
        <legend className={labelCls}>Best days / times for a call (optional)</legend>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Preferred days">
          {DAYS.map((d) => (
            <label key={d.value} className={chipCls}>
              <input type="checkbox" value={d.value} className="sr-only" {...register("preferred_days")} />
              {d.label}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Preferred times">
          {TIMES.map((t) => (
            <label key={t.value} className={chipCls}>
              <input type="checkbox" value={t.value} className="sr-only" {...register("preferred_times")} />
              {t.label} <span className="ml-1 text-xs text-ink-500">{t.hint}</span>
            </label>
          ))}
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="timezone" className={labelCls}>
              Your time zone
            </label>
            <input
              id="timezone"
              type="text"
              list="demo-timezones"
              autoComplete="off"
              className={inputCls}
              {...register("timezone")}
            />
            <datalist id="demo-timezones">
              {tzOptions.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor="availability_note" className={labelCls}>
              Other availability (optional)
            </label>
            <input
              id="availability_note"
              type="text"
              placeholder="e.g. Tues after 3pm, not the week of the 14th"
              aria-invalid={!!errors.availability_note}
              className={inputCls}
              {...register("availability_note")}
            />
            {errors.availability_note ? (
              <p className="mt-1 text-xs text-red-600">{errors.availability_note.message}</p>
            ) : null}
          </div>
        </div>
      </fieldset>

      <div>
        <label htmlFor="message" className={labelCls}>
          Anything we should know? (optional)
        </label>
        <textarea
          id="message"
          rows={5}
          className={inputCls}
          {...register("message")}
        />
      </div>

      {/* Optional, unchecked by default, and does not block submission — see
          the sms_consent history note above. */}
      <div className="rounded-lg border border-border bg-surface-muted px-4 py-3.5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-border accent-forest-700"
            {...register("sms_consent")}
          />
          <span className="text-xs text-ink-600 leading-relaxed">
            <strong className="text-ink-800">Text opt-in (optional).</strong>{" "}
            I agree to receive transactional and account-related text messages
            (SMS and RCS) from WeConsult Rx at the phone number above. Message
            frequency varies. Message and data rates may apply. Reply STOP to
            opt out or HELP for help. See our{" "}
            <a href="/sms-terms" className="underline">SMS &amp; RCS Terms</a>{" "}
            and{" "}
            <a href="/privacy" className="underline">Privacy Policy</a>. Mobile
            information and messaging consent are not shared with third parties
            or affiliates for marketing.
          </span>
        </label>
      </div>

      <TurnstileWidget onToken={setCaptchaToken} resetSignal={captchaReset} />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Button type="submit" disabled={isSubmitting} size="lg">
          {isSubmitting ? "Sending…" : "Request a demo"}
        </Button>
        <p className="text-xs text-ink-500">
          By submitting you agree to our{" "}
          <a href="/privacy" className="underline">privacy policy</a> and{" "}
          <a href="/terms" className="underline">terms of service</a>. No data
          sold to third parties.
        </p>
      </div>

      {status === "err" ? (
        <p role="alert" className="text-sm text-red-600">
          Something went wrong. Please email{" "}
          <a href="mailto:hello@weconsultrx.com" className="underline">hello@weconsultrx.com</a>.
        </p>
      ) : null}
    </form>
  );
}
